/**
 * Calendar Scheduler - Daily Capacity Packing Engine
 *
 * Implements effort-based packing to determine what tasks fit each day.
 * Key behavior:
 * - Only MY tasks (assigned to me or unassigned) consume daily capacity
 * - Waiting tasks (assigned to others) are shown separately
 * - Daily capacity = 1.0 (one day of effort)
 * - Missing estimates default to 0.25 (small task)
 */

import { PlanStep, StepStatus } from '../types/generated/graphql';

export type Segment = 'morning' | 'afternoon' | 'evening' | 'all-day';

export interface ScheduledStep {
  step: PlanStep;
  /** Suggested day to show this task */
  suggestedDay: Date;
  /** Time segment within the day */
  segment: Segment;
  /** Estimated effort in day units (0.0 - 1.0) */
  effort: number;
  /** True if step is hard-blocked (critical gate) */
  isBlocked: boolean;
  /** If soft-blocked, title of the step we're waiting on */
  waitingOnStep: string | null;
  /** Anchor hint if task relates to a key date (e.g., "Before billing date") */
  anchorHint: string | null;
  /** True if this is a waiting task (assigned to someone else) */
  isWaiting: boolean;
}

export interface DaySchedule {
  date: Date;
  dateKey: string; // YYYY-MM-DD format for easy lookup

  // My tasks (consume capacity)
  morningTasks: ScheduledStep[];
  afternoonTasks: ScheduledStep[];
  eveningTasks: ScheduledStep[];
  allDayTasks: ScheduledStep[];

  // Waiting tasks (don't consume capacity)
  waitingTasks: ScheduledStep[];

  totalEffort: number;       // Only MY tasks
  remainingCapacity: number; // DAILY_CAPACITY - totalEffort
}

export interface ScheduleResult {
  /** Map of stepId -> ScheduledStep */
  stepSchedule: Map<string, ScheduledStep>;
  /** Days with tasks, sorted chronologically */
  days: DaySchedule[];
  /** Anchor dates extracted from facts */
  anchors: Map<string, Date>;
}

// ============================================
// CONSTANTS
// ============================================

const DAILY_CAPACITY = 1.0;   // One day of effort
const DEFAULT_EFFORT = 0.25;  // Small task default for missing estimates
const MAX_DAYS = 365;         // Safety limit for scheduling

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get the start of today (midnight).
 */
function getToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/**
 * Format date as YYYY-MM-DD for map keys.
 */
function formatDateKey(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Add days to a date.
 */
function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Check if two dates are the same day.
 */
function isSameDay(a: Date, b: Date): boolean {
  return formatDateKey(a) === formatDateKey(b);
}

// ============================================
// EFFORT MODEL
// ============================================

/**
 * Convert estimated duration to effort units (0.0 - 1.0).
 * - 1 day estimate → 1.0 effort
 * - 0.5 day estimate → 0.5 effort
 * - No estimate → 0.25 (small task default)
 */
function getEffortUnits(step: PlanStep): number {
  const days = step.estimatedDurationDays;
  if (days && days > 0) {
    // Cap at 1.0 - larger tasks still fill one day max
    return Math.min(days, 1.0);
  }
  return DEFAULT_EFFORT; // 0.25 for missing estimates
}

/**
 * Determine segment based on effort.
 * - Large (>=0.75) → All Day
 * - Medium (>=0.5) → Afternoon
 * - Small (<=0.25) → Morning or Evening (alternating)
 */
function getSegmentForEffort(effort: number, smallTaskIndex: number): Segment {
  if (effort >= 0.75) return 'all-day';
  if (effort >= 0.5) return 'afternoon';
  // Small tasks alternate between morning and evening
  return smallTaskIndex % 2 === 0 ? 'morning' : 'evening';
}

// ============================================
// DEPENDENCY & BLOCKING LOGIC
// ============================================

/**
 * Topological sort of steps based on dependencies.
 */
function topologicalSort(steps: PlanStep[]): PlanStep[] {
  const stepMap = new Map(steps.map((s) => [s.id, s]));
  const visited = new Set<string>();
  const result: PlanStep[] = [];

  function visit(step: PlanStep) {
    if (visited.has(step.id)) return;
    visited.add(step.id);

    for (const depId of step.dependsOn || []) {
      const depStep = stepMap.get(depId);
      if (depStep) {
        visit(depStep);
      }
    }

    result.push(step);
  }

  for (const step of steps) {
    visit(step);
  }

  return result;
}

/**
 * Check if a step has all its dependencies completed or skipped.
 */
function areDependenciesSatisfied(step: PlanStep, stepMap: Map<string, PlanStep>): boolean {
  if (!step.dependsOn || step.dependsOn.length === 0) return true;

  for (const depId of step.dependsOn) {
    const depStep = stepMap.get(depId);
    if (depStep && depStep.status !== StepStatus.Completed && depStep.status !== StepStatus.Skipped) {
      return false;
    }
  }

  return true;
}

/**
 * Get the first incomplete dependency step.
 */
function getFirstIncompleteDependency(step: PlanStep, stepMap: Map<string, PlanStep>): PlanStep | null {
  if (!step.dependsOn || step.dependsOn.length === 0) return null;

  for (const depId of step.dependsOn) {
    const depStep = stepMap.get(depId);
    if (depStep && depStep.status !== StepStatus.Completed && depStep.status !== StepStatus.Skipped) {
      return depStep;
    }
  }

  return null;
}

/**
 * Check if a step is "hard blocked" - a critical gate.
 */
function isHardBlocked(step: PlanStep, stepMap: Map<string, PlanStep>): boolean {
  if (step.status === StepStatus.Completed || step.status === StepStatus.Skipped) {
    return false;
  }

  const metadata = step.metadata as Record<string, unknown> | null;
  if (metadata?.hardBlock === true) {
    return true;
  }

  const titleLower = step.title.toLowerCase();
  const isDecisionStep =
    titleLower.includes('choose') ||
    titleLower.includes('decide') ||
    titleLower.includes('select method') ||
    titleLower.includes('pick');

  if (isDecisionStep && !areDependenciesSatisfied(step, stepMap)) {
    return true;
  }

  return false;
}

// ============================================
// ANCHOR EXTRACTION
// ============================================

/**
 * Extract anchor dates from plan facts.
 */
function extractAnchors(facts: Record<string, unknown> | null | undefined): Map<string, Date> {
  const dates = new Map<string, Date>();

  if (!facts || typeof facts !== 'object') {
    return dates;
  }

  const dateKeywords = [
    'billingDate', 'nextBillingDate', 'dueDate', 'deadline',
    'renewalDate', 'expirationDate', 'contractDate', 'startDate', 'endDate',
    'cancelBy', 'cancelByDate'
  ];

  for (const [key, value] of Object.entries(facts)) {
    if (typeof value === 'string') {
      const keyLower = key.toLowerCase();
      const isDateKey = dateKeywords.some((k) => keyLower.includes(k.toLowerCase()));

      if (isDateKey || /date/i.test(key)) {
        const parsed = new Date(value);
        if (!isNaN(parsed.getTime())) {
          dates.set(key, parsed);
        }
      }
    }
  }

  return dates;
}

/**
 * Find an anchor hint for a step based on title/description keywords.
 */
function findAnchorHint(step: PlanStep, anchors: Map<string, Date>): string | null {
  const titleLower = step.title.toLowerCase();
  const descLower = step.description.toLowerCase();
  const text = titleLower + ' ' + descLower;

  const entries = Array.from(anchors.entries());
  for (const [key, date] of entries) {
    const keyLower = key.toLowerCase();
    if (text.includes(keyLower) || text.includes(keyLower.replace(/date/i, ''))) {
      const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `By ${formatted}`;
    }
  }

  if (text.includes('before billing') || text.includes('before next billing')) {
    const billingDate = anchors.get('nextBillingDate') || anchors.get('billingDate');
    if (billingDate) {
      const formatted = billingDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `Before billing (${formatted})`;
    }
  }

  return null;
}

// ============================================
// MAIN SCHEDULING FUNCTION
// ============================================

/**
 * Main scheduling function with capacity-based packing.
 *
 * Key behaviors:
 * 1. Only MY tasks (assigned to me OR unassigned) consume daily capacity
 * 2. Waiting tasks (assigned to others) are shown but don't consume capacity
 * 3. Tasks overflow to next day if capacity is full
 * 4. Each day has DAILY_CAPACITY = 1.0
 */
export function scheduleSteps(
  steps: PlanStep[],
  currentParticipantId: string | null,
  facts?: Record<string, unknown> | null
): ScheduleResult {
  const today = getToday();
  const stepMap = new Map(steps.map((s) => [s.id, s]));
  const anchors = extractAnchors(facts);

  // Separate tasks into my tasks vs waiting tasks
  const activeTasks = steps.filter(
    (s) => s.status !== StepStatus.Completed && s.status !== StepStatus.Skipped
  );

  const myTasks: PlanStep[] = [];
  const waitingTasks: PlanStep[] = [];

  for (const step of activeTasks) {
    const isWaiting = currentParticipantId
      ? step.assignedToId !== null && step.assignedToId !== currentParticipantId
      : false;

    if (isWaiting) {
      waitingTasks.push(step);
    } else {
      myTasks.push(step);
    }
  }

  // Sort my tasks topologically
  const sortedMyTasks = topologicalSort(myTasks);

  // Track capacity per day (index 0 = today, 1 = tomorrow, etc.)
  const dayCapacities: number[] = [];
  for (let i = 0; i < MAX_DAYS; i++) {
    dayCapacities.push(DAILY_CAPACITY);
  }

  // Track scheduled steps and day schedules
  const stepSchedule = new Map<string, ScheduledStep>();
  const daySchedules = new Map<string, DaySchedule>();

  // Counter for alternating small task segments
  let smallTaskIndex = 0;

  // Get or create a day schedule
  function getOrCreateDay(dayIndex: number): DaySchedule {
    const date = addDays(today, dayIndex);
    const key = formatDateKey(date);

    let day = daySchedules.get(key);
    if (!day) {
      day = {
        date,
        dateKey: key,
        morningTasks: [],
        afternoonTasks: [],
        eveningTasks: [],
        allDayTasks: [],
        waitingTasks: [],
        totalEffort: 0,
        remainingCapacity: DAILY_CAPACITY,
      };
      daySchedules.set(key, day);
    }
    return day;
  }

  // Find day index with capacity for given effort
  function findDayWithCapacity(startDayIndex: number, effort: number): number {
    for (let i = startDayIndex; i < MAX_DAYS; i++) {
      // If day has enough capacity, or it's empty and task fits
      if (dayCapacities[i] >= effort || (dayCapacities[i] === DAILY_CAPACITY && effort <= DAILY_CAPACITY)) {
        return i;
      }
    }
    return startDayIndex; // Fallback
  }

  // ============================================
  // PACK MY TASKS (consume capacity)
  // ============================================

  for (const step of sortedMyTasks) {
    const effort = getEffortUnits(step);
    const isBlocked = isHardBlocked(step, stepMap);
    const firstIncompleteDep = getFirstIncompleteDependency(step, stepMap);
    const waitingOnStep = !isBlocked && firstIncompleteDep ? firstIncompleteDep.title : null;
    const anchorHint = findAnchorHint(step, anchors);

    // Skip hard-blocked tasks from capacity packing (they'll be shown but grayed out)
    if (isBlocked) {
      // Blocked tasks go to today but don't consume capacity
      const day = getOrCreateDay(0);
      const segment = getSegmentForEffort(effort, smallTaskIndex++);

      const scheduledStep: ScheduledStep = {
        step,
        suggestedDay: day.date,
        segment,
        effort,
        isBlocked: true,
        waitingOnStep,
        anchorHint,
        isWaiting: false,
      };

      stepSchedule.set(step.id, scheduledStep);
      addToSegment(day, scheduledStep, segment);
      continue;
    }

    // Find earliest day this task can start (after dependencies)
    let earliestDayIndex = 0;
    for (const depId of step.dependsOn || []) {
      const scheduled = stepSchedule.get(depId);
      if (scheduled) {
        const depDayIndex = Math.floor(
          (scheduled.suggestedDay.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)
        );
        earliestDayIndex = Math.max(earliestDayIndex, depDayIndex + 1);
      }
    }

    // Find day with capacity
    const dayIndex = findDayWithCapacity(earliestDayIndex, effort);
    const day = getOrCreateDay(dayIndex);
    const segment = getSegmentForEffort(effort, smallTaskIndex++);

    // Create scheduled step
    const scheduledStep: ScheduledStep = {
      step,
      suggestedDay: day.date,
      segment,
      effort,
      isBlocked: false,
      waitingOnStep,
      anchorHint,
      isWaiting: false,
    };

    stepSchedule.set(step.id, scheduledStep);

    // Add to segment and subtract capacity
    addToSegment(day, scheduledStep, segment);
    dayCapacities[dayIndex] -= effort;
    day.totalEffort += effort;
    day.remainingCapacity = dayCapacities[dayIndex];
  }

  // ============================================
  // ASSIGN WAITING TASKS (don't consume capacity)
  // ============================================

  for (const step of waitingTasks) {
    const effort = getEffortUnits(step);
    const isBlocked = isHardBlocked(step, stepMap);
    const firstIncompleteDep = getFirstIncompleteDependency(step, stepMap);
    const waitingOnStep = !isBlocked && firstIncompleteDep ? firstIncompleteDep.title : null;
    const anchorHint = findAnchorHint(step, anchors);

    // Waiting tasks all go to today (they're shown but don't consume capacity)
    const day = getOrCreateDay(0);

    const scheduledStep: ScheduledStep = {
      step,
      suggestedDay: day.date,
      segment: 'morning', // Doesn't matter for waiting tasks
      effort,
      isBlocked,
      waitingOnStep,
      anchorHint,
      isWaiting: true,
    };

    stepSchedule.set(step.id, scheduledStep);
    day.waitingTasks.push(scheduledStep);
  }

  // Helper to add task to correct segment
  function addToSegment(day: DaySchedule, scheduledStep: ScheduledStep, segment: Segment) {
    switch (segment) {
      case 'morning':
        day.morningTasks.push(scheduledStep);
        break;
      case 'afternoon':
        day.afternoonTasks.push(scheduledStep);
        break;
      case 'evening':
        day.eveningTasks.push(scheduledStep);
        break;
      case 'all-day':
        day.allDayTasks.push(scheduledStep);
        break;
    }
  }

  // Convert day map to sorted array
  const days = Array.from(daySchedules.values()).sort(
    (a, b) => a.date.getTime() - b.date.getTime()
  );

  return {
    stepSchedule,
    days,
    anchors,
  };
}

// ============================================
// QUERY FUNCTIONS
// ============================================

/**
 * Get tasks for a specific date.
 */
export function getTasksForDate(
  result: ScheduleResult,
  date: Date
): DaySchedule | null {
  const key = formatDateKey(date);
  const day = result.days.find((d) => d.dateKey === key);
  return day || null;
}

/**
 * Get tasks for a date range.
 */
export function getTasksForDateRange(
  result: ScheduleResult,
  startDate: Date,
  endDate: Date
): DaySchedule[] {
  const startKey = formatDateKey(startDate);
  const endKey = formatDateKey(endDate);

  return result.days.filter((d) => d.dateKey >= startKey && d.dateKey <= endKey);
}

// ============================================
// FORMATTING FUNCTIONS
// ============================================

/**
 * Format effort for display (e.g., "~30m", "~2h", "~1d").
 */
export function formatEffort(effort: number): string {
  if (effort >= 1.0) {
    return `~${Math.round(effort)}d`;
  }
  if (effort >= 0.5) {
    return `~${Math.round(effort * 8)}h`;
  }
  // Small tasks
  const minutes = Math.round(effort * 8 * 60);
  if (minutes >= 60) {
    return `~${Math.round(minutes / 60)}h`;
  }
  return `~${minutes}m`;
}

/**
 * Format a date for display (e.g., "Today", "Tomorrow", "Dec 24").
 */
export function formatDayLabel(date: Date): string {
  const today = getToday();
  const tomorrow = addDays(today, 1);

  if (isSameDay(date, today)) {
    return 'Today';
  }
  if (isSameDay(date, tomorrow)) {
    return 'Tomorrow';
  }

  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Format date for header (e.g., "Today, Dec 22").
 */
export function formatDayHeader(date: Date): string {
  const today = getToday();
  const tomorrow = addDays(today, 1);
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  if (isSameDay(date, today)) {
    return `Today, ${dateStr}`;
  }
  if (isSameDay(date, tomorrow)) {
    return `Tomorrow, ${dateStr}`;
  }

  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}
